import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import { BackArrowSvg } from '../../../assets/event';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { RootStackParamList } from '../../navigator';
import DailyMissionsCard from '../../shell/header/DailyMissionsCard';
import HeaderWallet from '../../shell/header/HeaderWallet';
import Profile from '../../shell/header/Profile';
import RewardsCard from '../../shell/header/RewardsCard';
import ScaledBox from '../common/ScaledBox';
import { TouchableOpacity } from '../common/Touchable';
import { Nav } from '../index';
import { DESIGN_FRAME_WIDTH, getHeaderPillScale, HEADER_BAR, HEADER_PILL } from './headerBar';

type NavType = NativeStackNavigationProp<RootStackParamList>;

// The missions / free-chips pills are Home's alone; every other screen's header is just the bar.
const HEADER_CARDS_ROUTES = ['Main'];

// Screens the design gives a back arrow instead of the player's avatar. They lose the missions /
// free-chips cards with it - the header the design draws for them is just the arrow and the nav.
const BACK_BUTTON_ROUTES = ['Room', 'Tournament', 'ActiveTournament', 'Event'];

// Back-arrow screens that still carry the balance. The design's event Top Bar (3324:18867) has
// no avatar but keeps the wallet; the table screens keep neither.
const WALLET_ROUTES = ['Event'];

// The title the design prints beside the back arrow, where it has one.
const BACK_BUTTON_TITLES: Record<string, string> = {
	Event: 'EVENT',
};

// `cardsRow` is absolutely positioned, so on Home the missions / free-chips pills float over the
// hero instead of pushing it down - Home lays its hero out under them (HEADER_PILL). Every other
// screen's header is just this row.
export const HEADER_ROW_HEIGHT = 78;

// A back-arrow header has no cards under its bar, so a screen under it only clears the bar.
export const HEADER_BACK_ROW_HEIGHT = HEADER_BAR.top + HEADER_BAR.height;

const TopSection = ({ onOpenSidebar }: { onOpenSidebar: () => void }) => {
	const { goBack } = useNavigation<NavType>();
	const route = useRoute();
	const { width } = useSafeViewport();
	const isLogged = useSelector(selectIsLogged);
	const pillScale = getHeaderPillScale(width);
	const pillsRowWidth = (DESIGN_FRAME_WIDTH - 2 * HEADER_PILL.side) * pillScale;

	const showBackButton = BACK_BUTTON_ROUTES.includes(route.name);
	const backButtonTitle = BACK_BUTTON_TITLES[route.name];
	const showWallet = WALLET_ROUTES.includes(route.name);
	const showHeaderCards = HEADER_CARDS_ROUTES.includes(route.name);

	return (
		<View style={styles.row}>
			{showBackButton ? (
				<View style={styles.contentColumn}>
					<View style={styles.backRow}>
						<TouchableOpacity onPress={() => goBack()} style={styles.backButton}>
							<BackArrowSvg width={12} height={20} />
						</TouchableOpacity>
						{backButtonTitle ? (
							<Text numberOfLines={1} style={styles.backTitle}>
								{backButtonTitle}
							</Text>
						) : null}
						{showWallet && isLogged ? <HeaderWallet /> : null}
					</View>
				</View>
			) : (
				<View style={styles.contentColumn}>
					<View style={styles.leftColumn}>
						<Profile />
					</View>
				</View>
			)}

			{showHeaderCards ? (
				<View
					pointerEvents='box-none'
					style={[styles.cardsRow, { left: (width - pillsRowWidth) / 2, width: pillsRowWidth }]}
				>
					<ScaledBox width={HEADER_PILL.width} height={HEADER_PILL.height} scale={pillScale}>
						<DailyMissionsCard />
					</ScaledBox>
					<ScaledBox width={HEADER_PILL.width} height={HEADER_PILL.height} scale={pillScale}>
						<RewardsCard />
					</ScaledBox>
				</View>
			) : null}

			<Nav onOpenSidebar={onOpenSidebar} />
		</View>
	);
};

const styles = StyleSheet.create({
	row: {
		position: 'relative',
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		zIndex: 30,
		elevation: 30,
	},
	leftColumn: {
		height: HEADER_BAR.top + HEADER_BAR.height,
		paddingTop: HEADER_BAR.top,
		paddingHorizontal: HEADER_BAR.side,
		alignItems: 'flex-start',
	},
	contentColumn: {
		flex: 1,
	},
	cardsRow: {
		position: 'absolute',
		top: HEADER_PILL.top,
		flexDirection: 'row',
		justifyContent: 'space-between',
		zIndex: 20,
	},
	backRow: {
		height: HEADER_BAR.top + HEADER_BAR.height,
		paddingTop: HEADER_BAR.top,
		paddingLeft: HEADER_BAR.side,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
	},
	// Figma "Back" (3324:18882): a 40x40 gold-rimmed disc on the card gradient.
	backButton: {
		width: 40,
		height: 40,
		borderRadius: 20,
		borderWidth: 1,
		borderColor: '#FFF908',
		backgroundColor: '#4A1004',
		alignItems: 'center',
		justifyContent: 'center',
		shadowColor: '#FFB21A',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.55,
		shadowRadius: 5,
		elevation: 4,
	},
	// Figma "Top Bar" (3324:18867) sets the label in Sofia Sans Condensed.
	backTitle: {
		color: '#FFF5EB',
		fontFamily: 'Sofia Sans Condensed',
		fontSize: 13,
		fontWeight: '700',
		letterSpacing: 1.6,
	},
});

export default TopSection;
